/**
 * Playback failures split into two populations, and only one of them is a
 * defect anybody can act on.
 *
 * A slow connection, an aborted load, or a blocked autoplay is a property of
 * the *visitor's session*. It recurs on every affected play, so wiring it to a
 * Sentry event bills telemetry in proportion to traffic instead of in
 * proportion to the number of distinct problems — the same shape that made
 * `getAudioUrl` 70% of this project's error volume (TES-1977).
 *
 * A decode failure or an unsupported/missing source is a property of the
 * *asset*: that file is broken in the catalog. That is worth an event, but its
 * information content is one bit per file, not one per play. Reporting it once
 * per asset per page session carries the whole signal, and the
 * `check-audio-links` cron is the table-wide sweep for the same class of
 * defect.
 */

/**
 * `source-unusable` is code 4 before it has been adjudicated. It is not a
 * verdict — see `resolveMediaFailureKind`. It never reports on its own, so a
 * caller that classifies but forgets to adjudicate under-reports rather than
 * billing a session condition as a defect.
 */
export type AudioFailureKind =
  | 'session-condition'
  | 'asset-defect'
  | 'source-unusable'
  | 'unknown'

/** `HTMLMediaError.code` values, which are not exposed as constants off the class. */
const MEDIA_ERR_ABORTED = 1
const MEDIA_ERR_NETWORK = 2
const MEDIA_ERR_DECODE = 3
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4

/**
 * `play()` rejections that describe the visitor's environment. `AbortError`
 * never reaches here (the hook drops it as a non-fatal source switch), but it
 * is listed so a future caller that does pass it classifies the same way.
 */
const SESSION_CONDITION_ERROR_NAMES = new Set(['AbortError', 'NotAllowedError'])

/**
 * Code 3 is a verdict on its own: the browser received bytes and could not
 * decode them, which is a property of the file. Code 4 is not — it means *this
 * browser* refuses the source, which a reachable, valid PCM WAV still provokes
 * on some visitors (TES-2348: 13/13 census-named ids served HTTP 200 valid
 * RIFF/WAVE, and `check-audio-links` found 0 broken of 1804). It only becomes
 * an asset defect once the object is shown to be unreachable, which is what
 * `resolveMediaFailureKind` establishes.
 */
export function classifyMediaErrorCode(code: number): AudioFailureKind {
  switch (code) {
    case MEDIA_ERR_ABORTED:
    case MEDIA_ERR_NETWORK:
      return 'session-condition'
    case MEDIA_ERR_DECODE:
      return 'asset-defect'
    case MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'source-unusable'
    default:
      return 'unknown'
  }
}

/** Resolves whether the object behind a failing `src` is actually fetchable. */
export type AudioReachability = 'reachable' | 'unreachable' | 'indeterminate'

/**
 * A code-4 `src` is probed at most once per page session. The dedupe in
 * `shouldReportAudioFailure` runs *after* adjudication, so without this a
 * visitor whose browser refuses one file would issue a HEAD on every play.
 */
const reachabilityProbes = new Map<string, Promise<AudioReachability>>()

/**
 * The bucket is public and serves `access-control-allow-origin: *`, so a HEAD
 * from the page is enough. Anything that throws is a CORS or transport failure
 * that says nothing about the object, and a 5xx is the origin having a moment
 * rather than the file being broken — both are `indeterminate`. Only a 4xx
 * proves the object is not there, which is the quarantine shape
 * (`audio-files/quarantine-audio/` returned HTTP 400) that made code 4 a real
 * defect signal historically.
 */
export async function probeAudioReachability(src: string): Promise<AudioReachability> {
  try {
    // audio-egress-allow: HEAD request after a playback failure, no audio body
    const response = await fetch(src, { method: 'HEAD' })
    if (response.ok) return 'reachable'
    if (response.status >= 400 && response.status < 500) return 'unreachable'
    return 'indeterminate'
  } catch {
    return 'indeterminate'
  }
}

/**
 * The full verdict for a media error, including the reachability adjudication
 * code 4 needs. `probe` is injected for tests.
 */
export async function resolveMediaFailureKind(
  code: number,
  src: string | undefined,
  probe: (src: string) => Promise<AudioReachability> = probeAudioReachability
): Promise<AudioFailureKind> {
  const kind = classifyMediaErrorCode(code)
  if (kind !== 'source-unusable') return kind

  // No src to probe means no way to blame the asset, and no request worth
  // spending to find out.
  if (!src) return 'session-condition'

  let pending = reachabilityProbes.get(src)
  if (!pending) {
    pending = probe(src)
    reachabilityProbes.set(src, pending)
  }

  // An asset is only blamed once it is provably absent; a reachable or
  // unprovable object leaves the table-wide `check-audio-links` cron as the
  // authoritative detector rather than billing one event per affected visitor.
  return (await pending) === 'unreachable' ? 'asset-defect' : 'session-condition'
}

export function classifyPlayRejection(error: { name?: string; message?: string }): AudioFailureKind {
  if (error.name && SESSION_CONDITION_ERROR_NAMES.has(error.name)) return 'session-condition'
  // The hook's own 10s load timeout. A visitor on a slow link hits it on every
  // play, which is exactly the traffic-proportional shape to keep out of Sentry.
  if (error.message?.includes('timeout')) return 'session-condition'
  if (error.name === 'NotSupportedError') return 'asset-defect'
  return 'unknown'
}

/**
 * Distinct (kind, src) pairs already reported by this page session. Module
 * scope rather than a ref because the dedupe has to hold across every player
 * instance on the page — the /sounds grid mounts one per card, and a broken
 * asset in a shared queue would otherwise report once per card that touched it.
 */
const reported = new Set<string>()

/**
 * True the first time this (kind, src) pair fails in this page session.
 * `session-condition` never reports.
 */
export function shouldReportAudioFailure(kind: AudioFailureKind, src: string | undefined): boolean {
  if (kind === 'session-condition') return false
  // Un-adjudicated code 4. Reporting it here is exactly the misclassification
  // this split exists to prevent (TES-2348).
  if (kind === 'source-unusable') return false

  const key = `${kind}:${src ?? '<no-src>'}`
  if (reported.has(key)) return false
  reported.add(key)
  return true
}

/** Test seam — the dedupe set is module state and would leak between cases. */
export function resetAudioFailureReports(): void {
  reported.clear()
  reachabilityProbes.clear()
}

/** Sentry caps a tag value at 200 characters and truncates silently past it. */
const MAX_TAG_LENGTH = 200

/**
 * The storage path of a failing asset, as a Sentry *tag* so the cluster can be
 * grouped and searched by file rather than read one event at a time (TES-1874).
 *
 * The pathname only: a resolved audio URL can carry a signed token or a cache
 * buster, and either would give every play its own tag value and destroy the
 * grouping this exists for. The full URL still travels in `extra`.
 */
export function audioSrcTag(src: string | undefined): string {
  if (!src) return '<no-src>'
  let path = src
  try {
    path = new URL(src).pathname
  } catch {
    // Deliberately no base URL: a relative src (the bundled demo audio, the
    // proxy route) is already the path this wants, and anything else unparseable
    // is itself diagnostic. Either way, tag it verbatim rather than dropping the
    // only identifier we have.
  }
  return path.slice(0, MAX_TAG_LENGTH)
}
